import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import {
  fetchExpenseCategories,
  fetchFinancing,
  homeExpenseQueryKeys,
  patchFinancing,
} from "@apps/dashboard/lib/home-expense-api";
import type { HouseFinancingDetail } from "@apps/dashboard/lib/home-expense-types";
import { minorToMajorInputValue, parseAmountToMinor } from "@apps/dashboard/lib/parse-amount";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";

const fieldClass =
  "mt-2 w-full rounded-xl border-none bg-surface-container-low px-4 py-4 font-body text-on-surface outline-none transition-all duration-300 focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary";
const labelClass =
  "ml-1 block text-[0.7rem] font-bold uppercase tracking-widest text-on-surface-variant";

export default function FinancingEditPage(): JSX.Element {
  const { houseId, financingId } = useParams<{ houseId: string; financingId: string }>();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { houses } = useActiveHouse();
  const actorRole = houseId ? houses?.find((h) => h.id === houseId)?.role : undefined;
  const canEdit = actorRole === "owner" || actorRole === "admin";
  useSyncHouseFromParams(houseId);

  const [title, setTitle] = useState("");
  const [lender, setLender] = useState("");
  const [amountInput, setAmountInput] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [contractStartOn, setContractStartOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [successFlash, setSuccessFlash] = useState(false);

  const detailQuery = useQuery({
    queryKey:
      houseId && financingId
        ? homeExpenseQueryKeys.financingDetail(locale, houseId, financingId)
        : ["fin", "e"],
    queryFn: () => {
      if (!houseId || !financingId) throw new Error("missing");
      return fetchFinancing(locale, houseId, financingId);
    },
    enabled: Boolean(houseId && financingId),
  });

  const categoriesQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.categories(locale, houseId) : ["cat", "x"],
    queryFn: () => {
      if (!houseId) throw new Error("no house");
      return fetchExpenseCategories(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const d = detailQuery.data;
  useEffect(() => {
    if (!d) return;
    setTitle(d.title);
    setLender(d.lender ?? "");
    setAmountInput(minorToMajorInputValue(d.installmentAmountMinor));
    setCurrency(d.currency);
    setContractStartOn(d.contractStartOn);
    setEndsOn(d.endsOn);
    setCategoryId(d.categoryId ?? "");
    setNotes(d.notes ?? "");
  }, [d]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!houseId || !financingId) throw new Error("missing");
      const minor = parseAmountToMinor(amountInput.trim());
      if (minor === null) {
        throw new Error("amount");
      }
      return patchFinancing(locale, houseId, financingId, {
        title: title.trim(),
        lender: lender.trim() || null,
        installmentAmountMinor: minor,
        currency,
        contractStartOn,
        endsOn,
        categoryId: categoryId || null,
        notes: notes.trim() || null,
      });
    },
    onSuccess: async (updated: HouseFinancingDetail) => {
      if (!houseId || !financingId) return;
      setFormError(null);
      setSuccessFlash(true);
      toast.success(t("dashboardUi.financings.saveSuccess"));
      await queryClient.invalidateQueries({
        queryKey: homeExpenseQueryKeys.financingDetail(locale, houseId, financingId),
      });
      await queryClient.invalidateQueries({
        queryKey: homeExpenseQueryKeys.financingsList(locale, houseId),
      });
      if (updated.expenseSeriesId) {
        await queryClient.invalidateQueries({
          queryKey: homeExpenseQueryKeys.expenseSeries(locale, houseId, updated.expenseSeriesId),
        });
      }
      window.setTimeout(() => setSuccessFlash(false), 2500);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiRequestError) {
        setFormError(e.message);
        toast.error(e.message);
        return;
      }
      if (e instanceof Error && e.message === "amount") {
        const message = t("dashboardUi.expenses.form.invalidAmount");
        setFormError(message);
        toast.error(message);
        return;
      }
      const message = t("dashboardUi.states.errorHint");
      setFormError(message);
      toast.error(message);
    },
  });

  if (!houseId || !financingId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <DashboardBackLink to={dashboardPaths.houseFinancingDetail(houseId, financingId)}>
          {t("dashboardUi.financings.pageTitle")}
        </DashboardBackLink>
        <p className="mt-4 text-body-md text-on-surface-variant">{t("dashboardUi.vault.readOnlyHint")}</p>
      </div>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <DashboardBackLink to={dashboardPaths.houseFinancingDetail(houseId, financingId)}>
          {t("dashboardUi.financings.pageTitle")}
        </DashboardBackLink>
        <div className="mt-8">
          <LoadingState />
        </div>
      </div>
    );
  }

  if (detailQuery.isError || !d) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <DashboardBackLink to={dashboardPaths.houseFinancings(houseId)}>
          {t("dashboardUi.financings.pageTitle")}
        </DashboardBackLink>
        <div className="mt-8">
          <ErrorState />
        </div>
      </div>
    );
  }

  const categories = categoriesQuery.data ?? [];

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.houseFinancingDetail(houseId, financingId)}>
        {t("dashboardUi.financings.pageTitle")}
      </DashboardBackLink>

      <h1 className="mt-4 font-headline text-headline-sm font-bold text-primary">
        {t("dashboardUi.pages.financingEdit")}
      </h1>

      {successFlash ? (
        <p className="mt-4 rounded-xl bg-primary-container/40 px-4 py-3 text-body-sm text-on-surface" role="status">
          {t("dashboardUi.financings.saveSuccess")}
        </p>
      ) : null}

      {formError ? (
        <p className="mt-4 rounded-xl bg-error-container/30 px-4 py-3 text-body-sm text-error" role="alert">
          {formError}
        </p>
      ) : null}

      <form
        className="mt-8 space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          if (!title.trim()) {
            setFormError(t("validation.required"));
            return;
          }
          if (!endsOn.trim()) {
            setFormError(t("validation.required"));
            return;
          }
          void saveMutation.mutateAsync();
        }}
      >
        <div>
          <label className={labelClass} htmlFor="fin-edit-title">
            {t("dashboardUi.financings.titleLabel")}
          </label>
          <input id="fin-edit-title" className={fieldClass} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <label className={labelClass} htmlFor="fin-edit-lender">
            {t("dashboardUi.financings.lenderLabel")}
          </label>
          <input id="fin-edit-lender" className={fieldClass} value={lender} onChange={(e) => setLender(e.target.value)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="fin-edit-amount">
              {t("dashboardUi.financings.installmentLabel")}
            </label>
            <input
              id="fin-edit-amount"
              className={fieldClass}
              inputMode="decimal"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="fin-edit-ccy">
              {t("dashboardUi.expenses.form.currencyLabel")}
            </label>
            <input
              id="fin-edit-ccy"
              className={fieldClass}
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              maxLength={3}
            />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="fin-edit-start">
              {t("dashboardUi.financings.contractStartLabel")}
            </label>
            <input
              id="fin-edit-start"
              type="date"
              className={fieldClass}
              value={contractStartOn}
              onChange={(e) => setContractStartOn(e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="fin-edit-end">
              {t("dashboardUi.financings.endsOnLabel")}
            </label>
            <input id="fin-edit-end" type="date" className={fieldClass} value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
          </div>
        </div>
        <div>
          <label className={labelClass} htmlFor="fin-edit-cat">
            {t("dashboardUi.financings.categoryLabel")}
          </label>
          <select id="fin-edit-cat" className={fieldClass} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">{t("dashboardUi.financings.categoryNone")}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass} htmlFor="fin-edit-notes">
            {t("dashboardUi.financings.notesLabel")}
          </label>
          <textarea id="fin-edit-notes" className={fieldClass} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? t("dashboardUi.financings.saving") : t("dashboardUi.financings.saveCta")}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(dashboardPaths.houseFinancingDetail(houseId, financingId))}>
            {t("dashboardUi.financings.backCta")}
          </Button>
        </div>
      </form>
    </div>
  );
}
